import { ExperiencePremadeItem } from "@/composables/useExperienceMasterDataApi";
import { HierarchicalGroup } from "@/types/generated/ExperienceMasterDataApi";

export const UNCATEGORIZED_HIERARCHICAL_GROUP_KEY = "UNCATEGORIZED";
export const UNCATEGORIZED_HIERARCHICAL_GROUP_NAME = "Uncategorized";

export function mapHighlightsToGroups(
  items: ExperiencePremadeItem[],
  hierarchicalGroups: Map<string, HierarchicalGroup> = new Map()
): Map<string, ExperiencePremadeItem[]> {
  if (items.length === 0) {
    return new Map([[UNCATEGORIZED_HIERARCHICAL_GROUP_KEY, []]]);
  }

  const groups = new Map<string, ExperiencePremadeItem[]>();

  // Group items by hierarchical_group_code, mapping invalid groups to UNCATEGORIZED
  for (const item of items) {
    let groupKey = item?.hierarchical_group_code ?? UNCATEGORIZED_HIERARCHICAL_GROUP_KEY;

    // If item has a group code but it doesn't exist in hierarchicalGroups, map to UNCATEGORIZED
    if (groupKey !== UNCATEGORIZED_HIERARCHICAL_GROUP_KEY && !hierarchicalGroups.has(groupKey)) {
      groupKey = UNCATEGORIZED_HIERARCHICAL_GROUP_KEY;
    }

    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }
    groups.get(groupKey)!.push(item);
  }

  // Sort items within groups (archived last) and sort groups (archived categories last, then alphabetically)
  return new Map(
    Array.from(groups.entries())
      .map(([key, items]) => [key, items.toSorted((a, b) => Number(a.archived) - Number(b.archived))] as const)
      .sort(([codeA], [codeB]) => {
        const groupA = hierarchicalGroups.get(codeA);
        const groupB = hierarchicalGroups.get(codeB);

        const aIsUncat = codeA === UNCATEGORIZED_HIERARCHICAL_GROUP_KEY;
        const bIsUncat = codeB === UNCATEGORIZED_HIERARCHICAL_GROUP_KEY;

        // Treat UNCATEGORIZED as non-archived regardless of metadata
        const aArchived = aIsUncat ? false : Boolean(groupA?.archived);
        const bArchived = bIsUncat ? false : Boolean(groupB?.archived);

        // 1) Non-archived groups before archived groups
        if (aArchived !== bArchived) {
          return Number(aArchived) - Number(bArchived);
        }

        // 2) Among non-archived groups, UNCATEGORIZED should always be last
        if (!aArchived && !bArchived) {
          if (aIsUncat && !bIsUncat) return 1; // A goes after B
          if (bIsUncat && !aIsUncat) return -1; // B goes after A
        }

        // 3) Alphabetical ordering by group name (fallback to code)
        return (groupA?.name || codeA).localeCompare(groupB?.name || codeB);
      })
  );
}
